export default [
    {
        path: '/settings/account',
        name: 'Account',
        meta: {
            requiresAuth: true,
            allowRoles: null,
            denyRoles: null,
            title: 'Account',
        },
        components: {
            default: () => import('@/views/settings/AccountSettings.vue'),
        },
    },
    {
        path: '/settings/global',
        name: 'GlobalSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'MOVEPRO_ADMIN'],
            denyRoles: null,
            title: 'Global settings',
        },
        components: {
            default: () => import('@views/settings/GlobalSettings.vue'),
        },
    },
    {
        path: '/settings',
        name: 'TenantSettings',
        meta: {
            requiresAuth: true,
            // FINANCE is here because this is the index every settings page is
            // reached through, and the profile dropdown offers it to every tenant
            // user. Settings.vue filters the tiles against each target route's own
            // allowRoles, so a finance user lands here and sees Integrations only.
            allowRoles: ['SUPER', 'OWNER', 'ADMIN', 'FINANCE'],
            denyRoles: null,
            title: 'Settings',
        },
        components: {
            default: () => import('@views/settings/Settings.vue'),
        },
    },
    {
        path: '/settings/company',
        name: 'CompanySettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN'],
            denyRoles: null,
            title: 'Company settings',
        },
        components: {
            default: () => import('@views/settings/CompanySettings.vue'),
        },
    },
    {
        path: '/settings/legal',
        name: 'LegalSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN'],
            denyRoles: null,
            title: 'Legal settings',
        },
        components: {
            default: () => import('@views/settings/LegalSettings.vue'),
        },
    },
    {
        path: '/settings/documents',
        name: 'DocumentsPdfsSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN'],
            denyRoles: null,
            title: 'Documents & PDFs settings',
        },
        components: {
            default: () => import('@views/settings/DocumentsPdfsSettings.vue'),
        },
    },
    {
        path: '/settings/booking',
        name: 'BookingSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN'],
            denyRoles: null,
            title: 'Booking settings',
        },
        components: {
            default: () => import('@views/settings/BookingSettings.vue'),
        },
    },
    {
        path: '/settings/transport',
        name: 'TransportSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN'],
            denyRoles: null,
            title: 'Transport settings',
        },
        components: {
            default: () => import('@views/settings/TransportSettings.vue'),
        },
    },
    {
        path: '/settings/inventory',
        name: 'InventorySettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN', 'MANAGER'],
            denyRoles: null,
            title: 'Inventory settings',
        },
        components: {
            default: () => import('@views/settings/InventorySettings.vue'),
        },
    },
    {
        path: '/settings/crew',
        name: 'CrewSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN'],
            denyRoles: null,
            title: 'Crew settings',
        },
        components: {
            default: () => import('@views/settings/CrewSettings.vue'),
        },
    },
    {
        path: '/settings/integrations',
        name: 'IntegrationsSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN', 'FINANCE'],
            denyRoles: null,
            title: 'Integrations settings',
        },
        components: {
            default: () => import('@views/settings/IntegrationsSettings.vue'),
        },
    },
    {
        // Its own screen rather than a card on Integrations. Every section there
        // is gated (xero_enabled, email_enabled, open_api_enabled) and this one
        // must NOT be: a tenant whose leads only arrive through the CRM or a
        // booking form still fires these webhooks and may hold no API token.
        path: '/settings/webhooks',
        name: 'WebhooksSettings',
        meta: {
            requiresAuth: true,
            // Matches ApiKeysSettings and TenantWebhookController. Movepro staff
            // support a tenant's API keys, so they support their webhooks too.
            allowRoles: ['SUPER', 'MOVEPRO_ADMIN', 'OWNER', 'ADMIN'],
            denyRoles: null,
            title: 'Webhooks settings',
        },
        components: {
            default: () => import('@views/settings/WebhooksSettings.vue'),
        },
    },
    {
        path: '/settings/pricing',
        name: 'PricingSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN'],
            denyRoles: null,
            title: 'Pricing settings',
        },
        components: {
            default: () => import('@views/settings/PricingSettings.vue'),
        },
    },
    {
        path: '/settings/payments',
        name: 'StripeSettings',
        meta: {
            requiresAuth: true,
            // FINANCE is here because the customer invoice settings live on this
            // page, and maintaining those is finance's job. The profile dropdown
            // has always offered this route to finance as "Payment management";
            // it simply refused them (CU-86d42vpyy).
            //
            // An earlier version of this comment said StripeSettings.vue scoped the
            // page so finance got the invoice sections and not the Stripe account.
            // That scoping was removed from the branch before it merged, so the
            // comment described behaviour that never shipped. The page is now scoped
            // the other way: finance keeps the Stripe account panel, which is gated
            // on canManageStripeExternalAccount, and MANAGER and OPS do not reach
            // this route at all (CU-86d431060).
            allowRoles: ['SUPER', 'OWNER', 'ADMIN', 'FINANCE'],
            denyRoles: null,
            title: 'Payment settings',
        },
        components: {
            default: () => import('@views/settings/StripeSettings.vue'),
        },
    },
    {
        path: '/settings/tracking',
        name: 'TrackingSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN'],
            denyRoles: null,
            title: 'Tracking settings',
        },
        components: {
            default: () => import('@views/settings/TrackingSettings.vue'),
        },
    },
    {
        path: '/settings/telecommunications',
        name: 'TelcoSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN'],
            denyRoles: null,
            title: 'Telecommunications',
        },
        components: {
            default: () => import('@views/settings/TelcoSettings.vue'),
        },
    },
    {
        path: '/settings/security',
        name: 'SecuritySettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN'],
            denyRoles: null,
            title: 'Security settings',
        },
        components: {
            default: () => import('@views/settings/SecuritySettings.vue'),
        },
    },
    {
        // The user's own email signatures (CU-14zcqntr7dp): the roles that could
        // edit their personal signature on Account settings before.
        path: '/settings/email',
        name: 'EmailSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN', 'MANAGER', 'OPS', 'SALES'],
            denyRoles: null,
            title: 'Email settings',
        },
        components: {
            default: () => import('@views/settings/EmailSettings.vue'),
        },
    },
    {
        path: '/settings/communications',
        name: 'CommunicationsSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN', 'MANAGER'],
            denyRoles: null,
            title: 'Communications settings',
        },
        components: {
            default: () => import('@views/settings/CommunicationsSettings.vue'),
        },
    },
    {
        /**
         * Diary entry types carry `blocks_availability`, so this is settings rather
         * than day-to-day scheduling: Ops schedule all day but do not change what a
         * category means. Mirrors the `company-diary-manage-types` gate.
         */
        path: '/settings/company-diary-entry-types',
        name: 'CompanyDiaryEntryTypesSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'MOVEPRO_ADMIN', 'DEVELOPER', 'OWNER', 'ADMIN', 'MANAGER'],
            denyRoles: null,
            title: 'Diary entry types',
        },
        components: {
            default: () => import('@views/settings/CompanyDiaryEntryTypesSettings.vue'),
        },
    },
    {
        /**
         * Miscellaneous service templates. Mirrors the `manage-service-templates` gate, which
         * matches rate-card writes: Ops and Managers maintain the services they sell. Picking a
         * template on an order needs none of these roles.
         */
        path: '/settings/service-templates',
        name: 'ServiceTemplatesSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'MOVEPRO_ADMIN', 'DEVELOPER', 'OWNER', 'ADMIN', 'MANAGER', 'OPS'],
            denyRoles: null,
            title: 'Service templates',
        },
        components: {
            default: () => import('@views/settings/ServiceTemplatesSettings.vue'),
        },
    },
    {
        path: '/settings/rate-roles',
        name: 'RateRolesSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN', 'MANAGER'],
            denyRoles: null,
            title: 'Rate roles',
        },
        components: {
            default: () => import('@views/settings/RateRolesSettings.vue'),
        },
    },
    {
        path: '/settings/ai',
        name: 'AiSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN'],
            denyRoles: null,
            title: 'AI settings',
        },
        components: {
            default: () => import('@views/settings/TenantAiSettings.vue'),
        },
    },
    {
        path: '/settings/fix-list',
        name: 'FixListSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN'],
            denyRoles: null,
            title: 'Fix list settings',
        },
        components: {
            default: () => import('@views/settings/FixListSettings.vue'),
        },
    },
    {
        path: '/settings/api-keys',
        name: 'ApiKeysSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'MOVEPRO_ADMIN', 'OWNER', 'ADMIN'],
            denyRoles: null,
            title: 'API keys',
        },
        components: {
            default: () => import('@views/settings/ApiKeysSettings.vue'),
        },
    },
    {
        path: '/settings/notifications',
        name: 'NotificationsSettings',
        meta: {
            requiresAuth: true,
            allowRoles: ['SUPER', 'OWNER', 'ADMIN'],
            denyRoles: null,
            title: 'Notifications settings',
        },
        components: {
            default: () => import('@views/settings/NotificationsSettings.vue'),
        },
    },
];
